<template>
  <div :class="cellBlockClasses" :style="cellStyle">
    <div v-tooltip="tooltipConfig" :class="cellClasses" @click="goto">
      <div :class="cellTextClasses">
        {{ startTime }}
        <br />
        <b class="text-uppercase">{{ city }}</b>

        <div>
          <i>{{ serviceTypes }}</i>
        </div>

        <div v-if="appointment.duration >= 1.5" class="cell-block__icons">
          <follow-up-dice :job="appointment.job" classes="text-primary" />
          <font-awesome-icon :class="appointmentStateIconColor" :icon="appointmentStateIcon" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  inject: ["simpleMode"],
  props: ["appointment"],

  computed: {
    appointmentStateIconColor() {
      if (this.appointment.scrutinised) {
        return "text-primary";
      }

      switch (this.appointment.state) {
        case "Travelling":
          return "text-primary";

        case "In Progress":
          return "text-green-alt";

        case "Overdue":
          return "text-danger";

        case "Attended":
          return "text-success";

        case "Not Attended":
          return "text-danger";

        default:
          return "text-warning";
      }
    },

    appointmentStateIcon() {
      if (this.appointment.scrutinised) {
        return ["fas", "thumbs-up"];
      }

      switch (this.appointment.state) {
        case "Travelling":
          return ["fas", "shipping-fast"];

        case "In Progress":
          return ["fas", "wrench"];

        case "Overdue":
          return ["fas", "badge"];

        case "Attended":
          return ["fas", "badge-check"];

        case "Not Attended":
          return ["far", "ban"];

        default:
          return ["far", "badge"];
      }
    },

    tooltipContent() {
      const a = this.appointment;

      const truck =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="currentColor" d="M48 0C21.5 0 0 21.5 0 48L0 368c0 26.5 21.5 48 48 48l16 0c0 53 43 96 96 96s96-43 96-96l128 0c0 53 43 96 96 96s96-43 96-96l32 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l0-64 0-32 0-18.7c0-17-6.7-33.3-18.7-45.3L512 114.7c-12-12-28.3-18.7-45.3-18.7L416 96l0-48c0-26.5-21.5-48-48-48L48 0zM416 160l50.7 0L544 237.3l0 18.7-128 0 0-96zM112 416a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm368-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z"/></svg>';
      const phone =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.2 18.4-30 11.6-46.3l-40-96z"/></svg>';

      // Prepare values for overlay
      const jobId = this.$lodash.get(a, "job_id", "ID");
      const startTime = moment(a.time).format("ddd DD MMM YYYY@hh:mma");
      const duration = this.$lodash.get(a, "duration");
      const jobContact = this.$lodash.get(a, "job.job_contacts[0].name");
      const jobAddress = this.$lodash.get(a, "job.job_address.formatted");
      const appointmentType =
        this?.appointment?.type === "Site Visit" ? `${truck} Site Visit` : `${phone} Phone Consult`;
      const isRecurring = this.$lodash.get(a, "job.is_recurring") === 1;
      const scrutinised = this.$lodash.get(a, "scrutinised");
      const jobStatus = this.$lodash.get(a, "job.status").split(" ").join("-").toLowerCase();
      const services = a.job.service_types.map((service) => service.name).join(", ");
      const assistants = this.$lodash.get(a, "assistants");

      let assistantHtml = "";
      if (assistants && assistants.length) {
        const assistantString = assistants.map((assistant) => assistant.name).join(", ");
        assistantHtml = `<div class="availability-popover__assistants"><b>Assistants:</b> ${assistantString}</div>`;
      }

      const serviceTypesHtml = services
        ? `<div class="availability-popover__service-types">${services}</div>`
        : "";

      // This is not working right now because tooltip is not triggering fontawesome rendering
      const scrutinisedHtml = scrutinised
        ? '<div class="availability-popover__scrutinised"><svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="thumbs-up" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="svg-inline--fa fa-thumbs-up text-primary"><path fill="currentColor" d="M104 224H24c-13.255 0-24 10.745-24 24v240c0 13.255 10.745 24 24 24h80c13.255 0 24-10.745 24-24V248c0-13.255-10.745-24-24-24zM64 472c-13.255 0-24-10.745-24-24s10.745-24 24-24 24 10.745 24 24-10.745 24-24 24zM384 81.452c0 42.416-25.97 66.208-33.277 94.548h101.723c33.397 0 59.397 27.746 59.553 58.098.084 17.938-7.546 37.249-19.439 49.197l-.11.11c9.836 23.337 8.237 56.037-9.308 79.469 8.681 25.895-.069 57.704-16.382 74.757 4.298 17.598 2.244 32.575-6.148 44.632C440.202 511.587 389.616 512 346.839 512l-2.845-.001c-48.287-.017-87.806-17.598-119.56-31.725-15.957-7.099-36.821-15.887-52.651-16.178-6.54-.12-11.783-5.457-11.783-11.998v-213.77c0-3.2 1.282-6.271 3.558-8.521 39.614-39.144 56.648-80.587 89.117-113.111 14.804-14.832 20.188-37.236 25.393-58.902C282.515 39.293 291.817 0 312 0c24 0 72 8 72 81.452z" class=""></path></svg></div>'
        : "";

      const recurringHtml = isRecurring
        ? '<div class="availability-popover__recurring">[R]</div>'
        : "";

      return (
        `<div class="availability-popover__id">#${jobId}</div>` +
        `<div class="availability-popover__time">${startTime}, ${duration}h</div>` +
        `${assistantHtml}` +
        `<div class="availability-popover__contact">${jobContact}</div>` +
        `<div class="availability-popover__address">${jobAddress}</div>` +
        `<div class="availability-popover__type">${appointmentType}</div>` +
        `${serviceTypesHtml}` +
        `${scrutinisedHtml}` +
        `${recurringHtml}` +
        `<div class="availability-popover__status status-background">${jobStatus[0].toUpperCase()}</div>`
      );
    },

    tooltipConfig() {
      const jobStatus = this.$lodash
        .get(this.appointment, "job.status")
        .split(" ")
        .join("-")
        .toLowerCase();

      return {
        content: this.tooltipContent,
        classes: ["cell-tooltip-theme", `status-${jobStatus}`],
        placement: "bottom-end",
      };
    },

    cellStyle() {
      const length = this.appointment.duration > 13 ? 13 : this.appointment.duration;

      return {
        width: `calc(${length * 100.6}% + ${length + 1}px)`,
        left: moment(this.appointment.time).minute() === 0 ? "" : "50%",
        zIndex: 20,
      };
    },

    cellClasses() {
      const current =
        this.$store.state["tech-availability"].selectedAppointment === this.appointment.id;

      return [current ? "current" : "", "cell-tooltip", "busy", "status-background"];
    },

    cellTextClasses() {
      return [
        "cell-text",
        "status-border-dark",
        this.appointment.job.is_recurring ? "recurring" : "",
      ];
    },

    cellBlockClasses() {
      let classes = ["cell-block", this.$options.filters.jobStatusClass(this.appointment.job)];

      if (
        moment(this.appointment.time)
          .add(this.appointment.duration, "hours")
          .isBefore(moment().subtract(30, "minutes")) &&
        !this.appointment?.checked_out &&
        !this.appointment?.checkout_override
      ) {
        classes.push("status-checkout-late");
      }

      return classes;
    },

    startTime() {
      return moment(this.appointment.time).format("HH:mma");
    },

    city() {
      return this.$lodash.get(this.appointment, "job.job_address.city");
    },

    serviceTypes() {
      return this.appointment.job.service_types.map((serviceType) => serviceType.name).join(", ");
    },
  },

  methods: {
    goto() {
      if (this.simpleMode || !this.$can("navigate", "jobs")) {
        return;
      }

      this.$store.state["tech-availability"].selectedAppointment = this.appointment.id;

      this.$router.push({
        name: "jobs.edit",
        params: { id: this.appointment.job.id },
      });
    },
  },
};
</script>
